import * as Y from 'yjs';
import {
  DocumentId,
  SyncMessageType,
  encodeMessage,
  decodeMessage,
  WebSocketMessage,
} from '@kafka/shared';

export class YjsSync {
  public doc: Y.Doc;
  private ws: WebSocket | null = null;
  private documentId: DocumentId;
  private reconnectTimeout: number | null = null;
  private _isConnected = false;
  private onConnectionChangeCallback: ((connected: boolean) => void) | null = null;
  private onResetCallback: ((doc: Y.Doc) => void) | null = null;
  private onInitialSyncCompleteCallback: (() => void) | null = null;
  private initialSyncComplete = false;
  private reconnectAttempts = 0;

  constructor(documentId: DocumentId) {
    this.documentId = documentId;
    this.doc = new Y.Doc();
    this.setupUpdateListener();
  }

  private setupUpdateListener(): void {
    this.doc.on('update', (update: Uint8Array, origin: unknown) => {
      if (origin === 'remote') return;
      if (this._isConnected && this.initialSyncComplete) {
        this.send({
          type: SyncMessageType.UPDATE,
          documentId: this.documentId,
          update,
        });
      }
    });
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  onConnectionChange(callback: (connected: boolean) => void): void {
    this.onConnectionChangeCallback = callback;
    callback(this._isConnected);
  }

  onReset(callback: (doc: Y.Doc) => void): void {
    this.onResetCallback = callback;
  }

  onInitialSyncComplete(callback: () => void): void {
    this.onInitialSyncCompleteCallback = callback;
  }

  connect(): void {
    this.connectWebSocket();
  }

  private connectWebSocket(): void {
    // Use configured WebSocket URL or derive from current page location
    const configuredWsUrl = import.meta.env.VITE_WS_URL;
    let wsUrl: string;

    if (configuredWsUrl) {
      wsUrl = configuredWsUrl;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws`;
    }

    this.ws = new WebSocket(wsUrl);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this._isConnected = true;
      this.reconnectAttempts = 0;
      this.onConnectionChangeCallback?.(true);

      // Request sync with our current state vector
      this.send({
        type: SyncMessageType.SYNC_REQUEST,
        documentId: this.documentId,
        stateVector: Y.encodeStateVector(this.doc),
      });
    };

    this.ws.onmessage = (event) => {
      const message = decodeMessage(new Uint8Array(event.data as ArrayBuffer));
      this.handleMessage(message);
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected, reconnecting...');
      this._isConnected = false;
      this.initialSyncComplete = false;
      this.onConnectionChangeCallback?.(false);
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 15000);
    this.reconnectAttempts++;
    this.reconnectTimeout = window.setTimeout(() => {
      this.connectWebSocket();
    }, delay);
  }

  private send(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(encodeMessage(message));
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case SyncMessageType.SYNC_RESPONSE:
        this.handleSyncResponse(message.update, message.stateVector);
        break;
      case SyncMessageType.UPDATE:
        this.handleUpdate(message.update);
        break;
      case SyncMessageType.SYNC_RESET:
        this.handleSyncReset(message.update);
        break;
    }
  }

  private handleSyncResponse(update: Uint8Array, serverStateVector: Uint8Array): void {
    // Apply server's update (what we're missing)
    if (update.length > 0) {
      Y.applyUpdate(this.doc, update, 'remote');
    }

    // Send our updates that the server is missing
    const missingOnServer = Y.encodeStateAsUpdate(this.doc, serverStateVector);
    if (missingOnServer.length > 0) {
      this.send({
        type: SyncMessageType.UPDATE,
        documentId: this.documentId,
        update: missingOnServer,
      });
    }

    this.initialSyncComplete = true;
    this.onInitialSyncCompleteCallback?.();
    console.log('Initial sync complete');
  }

  private handleUpdate(update: Uint8Array): void {
    Y.applyUpdate(this.doc, update, 'remote');
  }

  private handleSyncReset(update: Uint8Array): void {
    console.warn('Server rejected update, resetting document...');

    // Create a fresh Yjs doc with the valid state from server
    const newDoc = new Y.Doc();
    Y.applyUpdate(newDoc, update, 'remote');

    // Replace the current doc
    this.doc.destroy();
    this.doc = newDoc;
    this.setupUpdateListener();

    // Notify the editor to rebind to the new doc
    this.onResetCallback?.(this.doc);
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.doc.destroy();
  }
}
